import { IAMapSetting, ICommonSettings } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

interface IAMapSettingFormData {
  name: string;
  apiKey: string;
  securityJsCode: string;
  webServiceKey?: string;
  enableWorldMap?: boolean;
}

export interface IConfigAMapSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingMaps?: IAMapSetting;
}

export interface IConfigAMapSettingsProps {
  formInputName: string[];
  closeModal: () => void;
  showModal: boolean;
  editingMapsId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigAMapSettings extends React.PureComponent<IConfigAMapSettingsProps, IConfigAMapSettingsState> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigAMapSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount(): void {
    this.loadMapsSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigAMapSettingsProps): void {
    if (prevProps.editingMapsId !== this.props.editingMapsId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadMapsSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadMapsSetting();
    }
  }

  loadMapsSetting = (): void => {
    if (!this.props.editingMapsId) return;

    const editingMaps: IAMapSetting | undefined = this.props.commonSettings.amapSettings?.find(
      (x) => x.id === this.props.editingMapsId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (!editingMaps) return;
    this.setState({ editingMaps }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingMaps);
    });
  };

  existingNameValidator = async (_rule: unknown, value: string): Promise<void> => {
    const amapSettings: IAMapSetting[] | null | undefined = this.props.commonSettings?.amapSettings;

    const isSettingAlreadyExist: boolean =
      (amapSettings?.some(
        (x) => x.name === value && x.id !== this.props.editingMapsId && !x.isGlobalCommonConfigOnProjectConfig
      ) ??
        false) ||
      value === "Default";

    if (isSettingAlreadyExist) throw new Error("Amap Settings Name is already exist.");
  };

  onFinish = (data: IAMapSettingFormData): void => {
    this.saveStatus.start();

    let obj: IAMapSetting;
    if (this.props.editingMapsId) {
      obj = {
        id: this.props.editingMapsId,
        name: data.name,
        apiKey: data.apiKey,
        securityJsCode: data.securityJsCode,
        webServiceKey: data.webServiceKey?.trim() || undefined,
        enableWorldMap: data.enableWorldMap === true
      };
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "amapSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      obj = {
        id: Guid.create().toString(),
        name: data.name,
        apiKey: data.apiKey?.trim(),
        securityJsCode: data.securityJsCode?.trim(),
        webServiceKey: data.webServiceKey?.trim() || undefined,
        enableWorldMap: data.enableWorldMap === true
      };
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "amapSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (): void => {};

  deleteMaps = (): void => {
    if (!this.props.editingMapsId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    // One Amap setting can be referenced by the web, Android and API (Web Service Key) sections;
    // deleting it must be blocked while any other section still points at it.
    const sectionFields: Record<string, string> = {
      webConfig: "amapSettingsId",
      androidConfig: "amapSettingsId",
      apiConfig: "amapWebServiceSettingsId"
    };
    const otherSections: string[][] = Object.keys(sectionFields)
      .filter((section: string): boolean => section !== this.props.formInputName[0])
      .map((section: string): string[] => [section, sectionFields[section]]);

    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingMapsId || "",
      configKey: "amapSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Amap setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      shouldControlOtherSections: {
        listOfControl: otherSections,
        onCannotDelete: () => {
          KMainFunctions.displayErrorNotification(
            "This Amap setting is used on another config section (Web, Android or API). Please discard it there before deleting."
          );
        }
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render(): React.ReactNode {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Amap Setting"
        secondHeader="Amap Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingMapsId ? this.deleteMaps : undefined}
            deleteTestId="amapSettingDeleteButton"
            submitTestId={`amapSetting${this.props.editingMapsId ? "Update" : "Create"}Button`}
            submitLabel={this.props.editingMapsId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configAmapForm"
          />
        }
      >
        <Form
          id="configAmapForm"
          name="amapSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("amapsettingspopup_amapsettings_description")}>
            <ConfigField label="Name" required tooltipContentKey="amapsettingspopup_amapsettings_name">
              <Form.Item
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please enter an Amap Setting Name."
                  },
                  {
                    pattern: /^[A-Za-z][A-Za-z0-9 ]+$/,
                    message: "Amap Setting Name is invalid"
                  },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="amapSettingsNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Api Key" required tooltipContentKey="amapsettingspopup_amapsettings_apikey">
              <Form.Item name="apiKey" rules={[{ required: true, message: "Please enter a Api Key." }]}>
                <KuikaInput size="md" data-testid="amapSettingApiKeyInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Security JS Code"
              required
              tooltipContentKey="amapsettingspopup_amapsettings_securityjscode"
            >
              <Form.Item
                name="securityJsCode"
                rules={[{ required: true, message: "Please enter a Security JS Code." }]}
              >
                <KuikaInput size="md" data-testid="amapSettingSecurityJsCodeInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField label="Web Service Key" tooltipContentKey="amapsettingspopup_amapsettings_webservicekey">
              <Form.Item name="webServiceKey">
                <KuikaInput
                  size="md"
                  data-testid="amapSettingWebServiceKeyInput"
                  placeholder="REST key for backend geocoding actions; falls back to Api Key when empty"
                />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="World Map"
              tooltipContentKey="amapsettingspopup_amapsettings_enableworldmap"
              hint="Overseas tiles and labels in the app language. Requires the World Map entitlement on this key."
            >
              <Form.Item name="enableWorldMap" valuePropName="checked">
                <KuikaSwitch dataTestId="amapSettingEnableWorldMapSwitch" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
